import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {MutationObserver | undefined} */
  #gridObserver;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    // The cards are rendered with the `sizes` of the grid's default density. If an
    // inline script restored a different saved density before this ran, the images
    // are still sized for the wrong one, so re-apply now — lazy images below the
    // fold have not started loading yet and will fetch at the corrected size.
    this.#applyImageSizes();
    this.#observeAppendedCards();
  }

  disconnectedCallback() {
    // PaginatedList tears down the infinite-scroll observer and the document-level
    // FilterUpdate listener here; without this call both survive the element.
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#gridObserver?.disconnect();
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      localStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
    this.#applyImageSizes();
  }

  /**
   * Copies the `sizes` value for the grid's current density onto every product
   * image. Each density draws cards at a different width, so without this the
   * browser keeps the file it picked for the previous density and upscales it,
   * which reads as blurry in the wider densities.
   *
   * @param {Element} [root] - Limit the update to this subtree. Defaults to the grid.
   */
  #applyImageSizes(root) {
    const { grid } = this.refs;
    if (!grid) return;

    const view = grid.getAttribute('product-grid-view');
    // Read with getAttribute rather than dataset: `data-sizes-cols-5` does not
    // camel-case to `sizesCols5`, because the dash is only dropped before a letter.
    const sizes = view && grid.getAttribute(`data-sizes-${view}`);
    if (!sizes) return;

    for (const image of (root ?? grid).querySelectorAll('img[srcset]')) {
      if (image.getAttribute('sizes') !== sizes) image.setAttribute('sizes', sizes);
    }
  }

  /**
   * Watches for cards appended by infinite scroll. They are rendered server-side at
   * the grid's default density, so they need the same correction as the initial set.
   */
  #observeAppendedCards() {
    const { grid } = this.refs;
    if (!grid) return;

    // childList only — the callback sets `sizes`, which would re-trigger an
    // attribute observer. Only the appended cards are touched, so the cost does
    // not grow with the number of pages already loaded.
    this.#gridObserver = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node instanceof Element) this.#applyImageSizes(node);
        }
      }
    });
    this.#gridObserver.observe(grid, { childList: true });
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const viewport = event.matches ? 'desktop' : 'mobile';
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    // Prefer whatever the shopper last chose at this breakpoint over the default,
    // so crossing the breakpoint restores their density instead of discarding it.
    const stored = localStorage.getItem(`product-grid-view-${viewport}`);
    const options = this.querySelectorAll(`input[type="radio"][name="${event.matches ? 'grid' : 'grid-mobile'}"]`);
    const restored = stored && [...options].some((option) => option.value === stored) ? stored : targetElement.value;

    for (const option of options) {
      if (option instanceof HTMLInputElement) option.checked = option.value === restored;
    }

    this.#setLayout(restored);
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
